import { useEffect, useState, useCallback } from "react";
import { chListCentros, chCrearCentro, chEditarCentro, chBorrarCentro } from "@/lib/prevelis";
import { toast } from "sonner";
import { Plus, Building, MapPin, Users, Pencil, Trash2, Loader2, X, Save } from "lucide-react";

const field = "w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none text-sm";
const EMPTY = { nombre: "", direccion: "", canton: "", responsable: "", num_trabajadores: "", actividad: "", descripcion: "" };

export default function SuizaCentros({ empresaId, cantones = [], onCount }) {
  const [centros, setCentros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    chListCentros(empresaId).then((c) => { setCentros(c); onCount?.(c.length); }).catch(() => {}).finally(() => setLoading(false));
  }, [empresaId, onCount]);
  useEffect(() => { load(); }, [load]);

  const abrirNuevo = () => { setForm({ ...EMPTY, canton: cantones[0] || "" }); setEditId(null); setOpen(true); };
  const abrirEditar = (c) => { setForm({ ...EMPTY, ...c }); setEditId(c.id); setOpen(true); };
  const guardar = async () => {
    if (!(form.nombre || "").trim()) { toast.error("El nombre del centro es obligatorio"); return; }
    setSaving(true);
    try {
      if (editId) { await chEditarCentro(editId, form); toast.success("Centro actualizado"); }
      else { await chCrearCentro(empresaId, form); toast.success("Centro creado"); }
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar"); }
    finally { setSaving(false); }
  };
  const borrar = async (c) => {
    if (!window.confirm(`¿Eliminar el centro "${c.nombre}"? Se borrarán también sus puestos e inspecciones.`)) return;
    try { await chBorrarCentro(c.id); toast.success("Centro eliminado"); load(); }
    catch { toast.error("No se pudo eliminar"); }
  };

  return (
    <div data-testid="ch-centros-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h3 className="font-heading font-semibold text-lg">Centros de trabajo</h3>
          <p className="text-sm text-slate-500">Cada centro agrupa sus propios puestos e inspecciones. Una empresa puede tener varios centros.</p>
        </div>
        <button onClick={abrirNuevo} data-testid="ch-centro-nuevo" className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-blue-700 text-white text-sm font-medium hover:bg-blue-800">
          <Plus className="w-4 h-4" /> Nuevo centro
        </button>
      </div>

      {loading ? (
        <div className="text-slate-400 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>
      ) : centros.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-8 text-center text-slate-500" data-testid="ch-centros-vacio">
          Aún no hay centros. Crea el primero para empezar a inspeccionar.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="ch-centros-lista">
          {centros.map((c) => (
            <div key={c.id} data-testid={`ch-centro-${c.id}`} className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center shrink-0"><Building className="w-4 h-4" /></div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-slate-900 truncate">{c.nombre}</div>
                  {c.actividad && <div className="text-xs text-slate-500 truncate">{c.actividad}</div>}
                  <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
                    {c.direccion && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {c.direccion}</span>}
                    <span className="inline-flex items-center gap-1 text-slate-400">{c.canton}</span>
                    {c.num_trabajadores !== "" && c.num_trabajadores != null && <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {c.num_trabajadores}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => abrirEditar(c)} data-testid={`ch-centro-editar-${c.id}`} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-500"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => borrar(c)} data-testid={`ch-centro-borrar-${c.id}`} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-red-600"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="ch-centro-dialog">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold text-lg">{editId ? "Editar centro" : "Nuevo centro"}</h3>
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div><label className="text-sm font-medium text-slate-700">Nombre del centro *</label>
                <input data-testid="ch-centro-nombre" value={form.nombre} onChange={(e) => setForm((s) => ({ ...s, nombre: e.target.value }))} className={field + " mt-1 h-11"} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-sm font-medium text-slate-700">Cantón</label>
                  <select data-testid="ch-centro-canton" value={form.canton} onChange={(e) => setForm((s) => ({ ...s, canton: e.target.value }))} className={field + " mt-1 h-11"}>
                    {cantones.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select></div>
                <div><label className="text-sm font-medium text-slate-700">Nº de trabajadores</label>
                  <input type="number" min="0" data-testid="ch-centro-num" value={form.num_trabajadores} onChange={(e) => setForm((s) => ({ ...s, num_trabajadores: e.target.value }))} className={field + " mt-1 h-11"} /></div>
              </div>
              <div><label className="text-sm font-medium text-slate-700">Dirección</label>
                <input data-testid="ch-centro-direccion" value={form.direccion} onChange={(e) => setForm((s) => ({ ...s, direccion: e.target.value }))} className={field + " mt-1 h-11"} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-sm font-medium text-slate-700">Actividad del centro</label>
                  <input data-testid="ch-centro-actividad" value={form.actividad} onChange={(e) => setForm((s) => ({ ...s, actividad: e.target.value }))} className={field + " mt-1 h-11"} /></div>
                <div><label className="text-sm font-medium text-slate-700">Responsable del centro</label>
                  <input data-testid="ch-centro-responsable" value={form.responsable} onChange={(e) => setForm((s) => ({ ...s, responsable: e.target.value }))} className={field + " mt-1 h-11"} /></div>
              </div>
              <div><label className="text-sm font-medium text-slate-700">Descripción / notas</label>
                <textarea data-testid="ch-centro-descripcion" rows={2} value={form.descripcion} onChange={(e) => setForm((s) => ({ ...s, descripcion: e.target.value }))} className={field + " mt-1"} /></div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setOpen(false)} className="h-11 px-4 rounded-lg border border-slate-300 font-medium hover:bg-slate-50">Cancelar</button>
              <button onClick={guardar} disabled={saving} data-testid="ch-centro-guardar" className="inline-flex items-center gap-2 h-11 px-5 rounded-lg bg-blue-700 text-white font-medium hover:bg-blue-800 disabled:opacity-50">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
